import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Sidebar from "./components/utils/Sidebar";
import Header from "./components/utils/Header";
import BottomNav from "./components/utils/BottomNav";
import Login from "./components/auth/Login";
import Register from "./components/auth/Register";
import Forgot from "./components/auth/Forgot";
import OAuthSuccess from "./components/auth/OAuthSuccess";
import CreateAccount from "./components/admin/CreateAccount";
import Dashboard from "./components/dashboard/Dashboard";
import ToDo from "./components/todo/ToDo";
import Toast from "./components/utils/Toast";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/utils/ProtectedRoute";
import { ROLES } from "./layouts/roles";
import api from "./api";
import "./App.css";
import Hierarchy from "./components/admin/Hierarchy";
import Members from "./components/admin/Members";
import QRPublicForm from "./components/utils/QRPublicForm";
import PartnersLayout from "./components/partners/PartnersLayout";
import OutreachLayout from "./components/outreach/OutreachLayout";
import PartnerFeedback from "./components/forms/PartnerFeedback";

function AppContent() {
  const location = useLocation();
  
  // Log every single render with current path
  console.log("🔄 [RENDER] AppContent rendering with path:", location.pathname);
  
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [toast, setToast] = React.useState(null);
  const [darkMode, setDarkMode] = React.useState(
    localStorage.getItem("darkMode") === "true"
  );
  const [preloadedTodoData, setPreloadedTodoData] = React.useState(null);
  const navigate = useNavigate();
  const { currentUser, logout, loading } = React.useContext(
    require("./contexts/AuthContext").AuthContext
  );
  const authPaths = ["/login", "/register", "/forgot", "/auth/success"];

  // Define public paths at the top level
  const isPublicPath = React.useMemo(() => {
    const pathname = location.pathname;
    const isQR = pathname.startsWith("/qr/") && !pathname.startsWith("/qr/generator");
    const isPartner = pathname.startsWith("/partner-feedback");
    const result = isQR || isPartner;
    
    console.log("📍 [useMemo] Calculating isPublicPath");
    console.log("   pathname:", pathname);
    console.log("   isQR:", isQR);
    console.log("   isPartner:", isPartner);
    console.log("   RESULT:", result);
    
    if (result) {
      console.log("🌐".repeat(20));
      console.log("🎉 PUBLIC PATH - NO AUTH NEEDED!");
      console.log("🌐".repeat(20));
    }
    return result;
  }, [location.pathname]);

  // Mapping paths to titles
  const pageTitleMap = {
    "/dashboard": "Dashboard",
    "/todo": "To-Do",
    "/login": "Login",
    "/register": "Register",
    "/forgot": "Forgot Password",
    "/auth/success": "OAuth Success",
    "/admin/create-account": "Create Account",
    "/members": "Members",
  };

  // Dynamic page title for nested routes
  let pageTitle = pageTitleMap[location.pathname];
  if (!pageTitle) {
    if (location.pathname.startsWith("/outreach/people")) {
      pageTitle = "People";
    } else if (location.pathname.startsWith("/outreach/companies")) {
      pageTitle = "Companies";
    } else if (location.pathname.startsWith("/outreach/sales")) {
      pageTitle = "Sales";
    } else if (location.pathname.startsWith("/outreach/states")) {
      pageTitle = "States";
    } else if (location.pathname.startsWith("/outreach/email-templates")) {
      pageTitle = "Email Templates";
    } else if (location.pathname.startsWith("/outreach")) {
      pageTitle = "Outreach";
    } else if (location.pathname.startsWith("/partners/manager")) {
      pageTitle = "Partners";
    } else if (location.pathname.startsWith("/partners/qr-generator")) {
      pageTitle = "QR Generator";
    } else if (location.pathname.startsWith("/partners/onebox-manager")) {
      pageTitle = "ONEbox Manager";
    } else if (location.pathname.startsWith("/partners")) {
      pageTitle = "Partners";
    } else if (location.pathname.startsWith("/qr/") && !location.pathname.startsWith("/qr/generator")) {
      pageTitle = "QR Form";
    } else if (location.pathname.startsWith("/partner-feedback")) {
      pageTitle = "Partner Feedback";
    } else {
      pageTitle = "R3";
    }
  }

  // Separate effect that only handles public paths - runs FIRST
  React.useEffect(() => {
    if (isPublicPath) {
      console.log("=".repeat(60));
      console.log("[APP] 🌐 PUBLIC PATH - NO AUTH REQUIRED");
      console.log("[APP] Path:", location.pathname);
      console.log("=".repeat(60));
    }
  }, [isPublicPath, location.pathname]);

  // Main auth redirect effect - runs AFTER public path check
  React.useEffect(() => {
    // ABSOLUTE FIRST CHECK - before any logging
    const pathname = location.pathname;
    const isQR = pathname.startsWith("/qr/") && !pathname.startsWith("/qr/generator");
    const isPartner = pathname.startsWith("/partner-feedback");
    
    if (isQR || isPartner) {
      console.log("🚫🚫🚫 BLOCKING AUTH CHECK - PUBLIC PATH 🚫🚫🚫");
      return;
    }
    
    console.log("=".repeat(60));
    console.log("[APP] 🔍 REDIRECT CHECK");
    console.log("[APP] Path:", location.pathname);
    console.log("[APP] Loading:", loading);
    console.log("[APP] CurrentUser:", currentUser?.username || currentUser?.email || "null");
    console.log("[APP] isPublicPath:", isPublicPath);
    
    // Don't redirect while still loading user from localStorage/session
    if (loading) {
      console.log("[APP] Still loading, skipping redirect check");
      console.log("=".repeat(60));
      return;
    }
    
    // If user is already logged in and on an auth page (login/register), redirect to dashboard
    const isAuthPage = ["/login", "/register", "/forgot"].includes(location.pathname);
    if (currentUser && isAuthPage) {
      console.log("[APP] User is authenticated but on auth page - redirecting to dashboard");
      navigate("/dashboard", { replace: true });
      return;
    }
    
    // Check other auth requirements
    const isAuthPath = authPaths.includes(location.pathname);
    
    console.log("[APP] Auth Check:", {
      pathname: location.pathname,
      currentUser: !!currentUser,
      isAuthPath
    });
    
    if (!currentUser && !isAuthPath) {
      console.log("[APP] ❌ No user and not on auth path");
      console.log("[APP] 🚨🚨🚨 REDIRECTING TO /login from:", location.pathname);
      console.log("[APP] 🚨 STACK TRACE:", new Error().stack);
      try {
        const redirectTo = location.pathname + location.search + location.hash;
        localStorage.setItem("postLoginRedirect", redirectTo);
        console.log("[APP] Saved redirect target:", redirectTo);
      } catch (_) {}
      console.log("=".repeat(60));
      navigate("/login", { replace: true });
    } else {
      console.log("[APP] ✅ Access allowed -", 
        currentUser ? "User authenticated" : "Auth page");
      console.log("=".repeat(60));
    }
  }, [currentUser, location.pathname, navigate, loading, isPublicPath, authPaths]);

  // Function to refresh badge count
  const refreshTodoBadge = React.useCallback(async () => {
    if (!currentUser) return;

    try {
      console.log("[APP] Refreshing To-Do badge count...");
      const [calendarRes, emailsRes, peopleRes, labelsRes, dismissedRes] = await Promise.all([
        api.get("/google/calendar/events").catch(() => ({ data: [] })),
        api.get("/google/gmail/messages").catch(() => ({ data: [] })),
        api.get("/people").catch(() => ({ data: [] })),
        api.get("/google/gmail/labels").catch(() => ({ data: [] })),
        api.get("/google/gmail/dismissed").catch(() => ({ data: [] })),
      ]);

      const data = {
        calendar: calendarRes.data || [],
        emails: emailsRes.data || [],
        people: peopleRes.data || [],
        labels: labelsRes.data || [],
        dismissed: dismissedRes.data || [],
        timestamp: Date.now(),
      };

      // Calculate task count for badge
      const taskCount = calculateTodoTaskCount(data, currentUser);
      data.taskCount = taskCount;

      setPreloadedTodoData(data);
      console.log("[APP] To-Do badge refreshed successfully, tasks:", taskCount);
    } catch (error) {
      console.error("[APP] Error refreshing To-Do badge:", error);
    }
  }, [currentUser]);

  // Preload To-Do data in the background when user is authenticated
  React.useEffect(() => {
    if (!currentUser || loading || preloadedTodoData) return;

    // Delay preload slightly to let initial page load complete
    const timer = setTimeout(refreshTodoBadge, 1000);
    return () => clearTimeout(timer);
  }, [currentUser, loading, preloadedTodoData, refreshTodoBadge]);

  // Calculate To-Do task count for badge
  // Match the default filters in ToDo component: 3 days for emails, today only for follow-ups, assigned to "me"
  const calculateTodoTaskCount = (data, user) => {
    let count = 0;

    // Today's meetings
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const todaysMeetings = (data.calendar || []).filter(event => {
      const start = new Date(event.start?.dateTime || event.start?.date);
      return start >= today && start < tomorrow;
    });
    count += todaysMeetings.length;

    // Unresponded emails (3 days - matches ToDo default daysThreshold)
    const dismissedIds = new Set(data.dismissed || []);
    const cutoff = Date.now() - 3 * 24 * 60 * 60 * 1000;
    const unrespondedEmails = (data.emails || []).filter(email => {
      if (dismissedIds.has(email.id)) return false;
      if (Number(email.internalDate) < cutoff) return false;
      return true;
    });
    count += unrespondedEmails.length;

    // Follow-ups (today only, assigned to me - matches ToDo default filters)
    today.setHours(0, 0, 0, 0);
    const todayFollowUps = (data.people || []).filter(p => {
      if (!p.follow_up_date) return false;
      // Default filter is "me" - only count follow-ups assigned to current user
      if (p.assigned_to !== user?.id) return false;
      const followUpDate = new Date(p.follow_up_date);
      followUpDate.setHours(0, 0, 0, 0);
      return followUpDate.getTime() === today.getTime();
    });
    count += todayFollowUps.length;

    return count;
  };

  const showToast = (message, type) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const toggleDarkMode = () => {
    setDarkMode((prev) => {
      const newMode = !prev;
      localStorage.setItem("darkMode", newMode);
      return newMode;
    });
  };

  return (
    <div
      className={`app-container ${isExpanded ? "expanded" : ""} ${
        darkMode ? "dark-theme" : ""
      }`}
    >
      {!authPaths.includes(location.pathname) && 
       !(location.pathname.startsWith("/qr/") && !location.pathname.startsWith("/qr/generator")) && 
       !location.pathname.startsWith("/partner-feedback") && (
        <>
          <Sidebar isExpanded={isExpanded} setIsExpanded={setIsExpanded} currentUser={currentUser} todoTaskCount={preloadedTodoData?.taskCount || 0} />
          <Header
            isExpanded={isExpanded}
            onLogout={handleLogout}
            pageTitle={pageTitle}
            darkMode={darkMode}
            toggleDarkMode={toggleDarkMode}
            currentUser={currentUser}
          />
        </>
      )}
      <Routes>
        {/* Auth routes without main-content wrapper */}
        <Route path="/login" element={<Login showToast={showToast} />} />
        <Route path="/register" element={<Register showToast={showToast} />} />
        <Route path="/forgot" element={<Forgot showToast={showToast} />} />
        <Route path="/auth/success" element={<OAuthSuccess />} />
        
        {/* ⚠️ PUBLIC ROUTES - NO AUTH REQUIRED ⚠️ */}
        <Route path="/qr/:id" element={<QRPublicForm showToast={showToast} />} />
        <Route 
          path="/partner-feedback" 
          element={
            <>
              {console.log("[ROUTE] 🎯 Rendering PartnerFeedback component")}
              <PartnerFeedback showToast={showToast} />
            </>
          } 
        />

        {/* Protected routes with main-content wrapper */}
        <Route path="/admin/create-account" element={
          <div className={`main-content ${darkMode ? 'dark-theme' : ''}`}>
            <div className="page-content">
              <ProtectedRoute requiredRole={ROLES.ADMIN} currentUser={currentUser}>
                <CreateAccount showToast={showToast} />
              </ProtectedRoute>
            </div>
          </div>
        } />
        <Route path="/members" element={
          <div className={`main-content ${darkMode ? 'dark-theme' : ''}`}>
            <div className="page-content">
              <ProtectedRoute requiredRole={ROLES.ADMIN} currentUser={currentUser}>
                <Members showToast={showToast} />
              </ProtectedRoute>
            </div>
          </div>
        } />
        <Route path="/dashboard" element={
          <div className={`main-content ${darkMode ? 'dark-theme' : ''}`}>
            <div className="page-content">
              <ProtectedRoute requiredRole={ROLES.USER} currentUser={currentUser}>
                <Dashboard showToast={showToast} currentUser={currentUser} />
              </ProtectedRoute>
            </div>
          </div>
        } />
        <Route path="/outreach/*" element={
          <div className={`main-content ${darkMode ? 'dark-theme' : ''}`}>
            <div className="page-content" style={{ padding: 0 }}>
              <ProtectedRoute requiredRole={ROLES.USER} currentUser={currentUser}>
                <OutreachLayout showToast={showToast} currentUser={currentUser} />
              </ProtectedRoute>
            </div>
          </div>
        } />
        <Route path="/todo" element={
          <div className={`main-content ${darkMode ? 'dark-theme' : ''}`}>
            <div className="page-content">
              <ProtectedRoute requiredRole={ROLES.USER} currentUser={currentUser}>
                <ToDo showToast={showToast} currentUser={currentUser} preloadedData={preloadedTodoData} onTaskUpdate={refreshTodoBadge} />
              </ProtectedRoute>
            </div>
          </div>
        } />
        <Route path="/partners/*" element={
          <div className={`main-content ${darkMode ? 'dark-theme' : ''}`}>
            <div className="page-content" style={{ padding: 0 }}>
              <ProtectedRoute requiredRole={ROLES.USER} currentUser={currentUser}>
                <PartnersLayout showToast={showToast} currentUser={currentUser} />
              </ProtectedRoute>
            </div>
          </div>
        } />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      {currentUser && !authPaths.includes(location.pathname) && !(location.pathname.startsWith("/qr/") && !location.pathname.startsWith("/qr/generator")) && !location.pathname.startsWith("/partner-feedback") && <BottomNav currentUser={currentUser} todoTaskCount={preloadedTodoData?.taskCount || 0} />}
    </div>
  );
}

function App() {
  return (
    <DndProvider backend={HTML5Backend}>
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
    </DndProvider>
  );
}

export default App;
